// How a league's matches are scored.
//
// Shared so the admin list, the definer dialog and the public page all describe
// a league the same way — three screens phrasing the same rules differently is
// how players end up arguing about the format.
//
// The format is a CHOICE FROM A LIST, not two free numbers. "Sets per match"
// and "play to" could express 2 sets to 19, which nobody plays, and could not
// express 21/21/15, which everybody does — a deciding set is shorter than the
// two before it, and that needs a target per set rather than one for all of
// them. A fixed list says exactly what the club runs and nothing else.

/**
 * Every format a league can be scored under.
 *
 * `targets` is per set, which is the whole reason this is a list: the last
 * entry of 21/21/15 is a different number from the two before it.
 *
 * Ids are stored on the league and must stay stable — changing one orphans
 * every league already using it. Labels are display only and can be reworded.
 */
export const MATCH_FORMATS = [
  { id: 'set-15', label: 'Set to 15', targets: [15] },
  { id: 'set-21', label: 'Set to 21', targets: [21] },
  { id: 'set-28', label: 'Set to 28', targets: [28] },
  { id: 'sets2-15', label: '2 sets to 15', targets: [15, 15] },
  { id: 'sets2-21', label: '2 sets to 21', targets: [21, 21] },
  { id: 'match-15-15-15', label: 'Match 15/15/15', targets: [15, 15, 15] },
  { id: 'match-21-21-15', label: 'Match 21/21/15', targets: [21, 21, 15] },
];

export const DEFAULT_MATCH_FORMAT = 'set-21';

const byId = Object.fromEntries(MATCH_FORMATS.map(f => [f.id, f]));

/**
 * The format a league is played under, from either the stored id or the older
 * sets/playTo pair.
 *
 * Leagues defined before the list existed hold two numbers. Rather than migrate
 * them, they are matched onto the list here — 2 and 21 is exactly "2 sets to
 * 21". A pair that matches nothing (2 sets to 19, say) yields a one-off entry
 * describing what was actually set, because showing a league a format it is not
 * playing is worse than showing one that is not on the menu.
 *
 * Never returns null: a league with none of these fields predates all of them
 * and really was a single set to 21.
 */
export function resolveMatchFormat(league) {
  const stored = byId[league?.matchFormat];
  if (stored) return stored;

  const sets = Number(league?.sets) || 0;
  const playTo = Number(league?.playTo) || 0;
  if (!sets && !playTo) return byId[DEFAULT_MATCH_FORMAT];

  const s = sets || 1;
  const to = playTo || 21;
  const match = MATCH_FORMATS.find(f =>
    f.targets.length === s && f.targets.every(x => x === to));
  if (match) return match;

  return {
    id: '', legacy: true,
    label: s > 1 ? `${s} sets to ${to}` : `Set to ${to}`,
    targets: Array(s).fill(to),
  };
}

/** How many sets a match runs under this league's format. */
export const setsIn = (league) => resolveMatchFormat(league).targets.length;

/** True when the league is scored over more than one set per match. */
export const isMultiSet = (league) => setsIn(league) > 1;

/**
 * How many points past the goal a set may run before win-by-two stops applying.
 *
 * An OFFSET, not a score. A single number like 23 only means anything against a
 * 21-point set: on 21/21/15 it would sit eight points beyond the decider and
 * never bite, so the same league needed two different caps and had nowhere to
 * put the second. "Two past the goal" is one number that reads correctly
 * against every set in every format.
 *
 * Leagues written before this held the absolute score, so one is converted on
 * read by subtracting the opening set's target — 23 against 21 is 2 — rather
 * than migrated. A legacy value at or below the target capped nothing, and
 * still doesn't.
 */
export function capOver(league) {
  const over = Number(league?.capOver);
  if (Number.isFinite(over) && over > 0) return Math.floor(over);
  const legacy = Number(league?.cap) || 0;
  if (legacy <= 0) return 0;
  const opening = resolveMatchFormat(league).targets[0];
  return legacy > opening ? legacy - opening : 0;
}

/** The score that ends each set, in order. Empty when the league has no cap. */
export function capsFor(league) {
  const over = capOver(league);
  return over ? resolveMatchFormat(league).targets.map(t => t + over) : [];
}

/**
 * One-line description of a league's match format, cap included.
 *
 * The cap is the score at which win-by-two stops applying: to 21 capped two
 * past can finish 23-22, and that point ends it however close the game is. No
 * cap means the set runs on until someone leads by two, which is spelled out
 * rather than left blank — "no cap" is a rule, not a missing value.
 *
 * Caps are listed per set only when they differ, so 21/21/15 reads
 * "cap 23/23/17" while two sets to 21 stays "cap 23" instead of "23/23".
 *
 * Says "2 sets", never "best of 2": an even number of sets can be drawn, and
 * whether a league plays all of them or stops early is the admin's business,
 * not something to assert here.
 */
export function describeLeagueFormat(league) {
  const fmt = resolveMatchFormat(league);
  const caps = capsFor(league);
  if (!caps.length) return `${fmt.label}, win by 2, no cap`;
  const uniform = caps.every(c => c === caps[0]);
  return `${fmt.label}, win by 2, cap ${uniform ? caps[0] : caps.join('/')}`;
}
